import React from 'react';

import { formatMoney } from '@betterment/core';
import { useGraphQLQuery } from '@betterment/graphql';
import { StepProps, useAutoNavigate } from '@betterment/js-runtime';
import {
  Banner,
  Box,
  Button,
  Card,
  H1,
  H2,
  HStack,
  Heading,
  Icon,
  Separator,
  Text,
  VStack,
} from '@betterment/samba-react';
import { vstack } from '@betterment/samba-styled-system/patterns';

import { formatTaxFilingStatus } from '../../Shared/graphql/utils';
import { WorkflowLayout } from '../../Shared/layout/WorkflowLayout';
import { FinancialProfileDetailsDocument } from '../graphql/query.financialProfileDetails.generated';

export const InformationConfirmationStep: React.FC<StepProps> = ({ goToNextStep, goToPreviousStep, flowState }) => {
  const navigate = useAutoNavigate();

  const { data } = useGraphQLQuery(FinancialProfileDetailsDocument, {
    throwOnError: true,
  });

  if (!data?.financialProfile) return null;

  const financialProfile = data?.financialProfile;
  const residentialAddress = data?.user.residentialAddress;
  const bettermentBalanceCents = data?.netWorth?.internalSubtotal?.balance
    ? data?.netWorth?.internalSubtotal?.balance.cents
    : 0;

  const employmentStatus = financialProfile?.profession.employmentStatus;
  const occupation = financialProfile?.profession.occupation ? financialProfile?.profession.occupation : '-';

  const annualPretaxIncome = formatMoney(financialProfile?.annualPretaxIncome, 'defaultDollar');

  const spouseAnnualPretaxIncome = financialProfile?.spouse?.annualPretaxIncome
    ? formatMoney(financialProfile.spouse.annualPretaxIncome, 'defaultDollar')
    : '-';
  const householdInvestableAssets = formatMoney(financialProfile?.householdInvestableAssets, 'defaultDollar');
  const taxFilingStatus = formatTaxFilingStatus(financialProfile?.taxFilingStatus);

  const shouldShowBalanceWarning =
    bettermentBalanceCents < 10000000 && financialProfile?.householdInvestableAssets.cents < 10000000;

  return (
    <WorkflowLayout
      feature="advisor_network"
      pageTitle="Confirm Your Information"
      workflowTitle=""
      onBack={goToPreviousStep}
      onClose={() => navigate(flowState.returnToPath)}
      size="full"
    >
      <Box
        css={{
          display: 'grid',
          gap: 'space.400',
          alignSelf: 'center',
          paddingTop: 'space.100',
          maxWidth: '540px',
          smDown: {
            display: 'grid',
            gap: 'space.200',
            maxWidth: '100%',
          },
        }}
      >
        <Box>
          <VStack css={{ gap: 'space.200' }}>
            <H1 variant="pageTitle">Confirm your information.</H1>
            <Text>Our specialist team will use this to connect you with the right advisors.</Text>
          </VStack>
        </Box>
        <Card
          css={{
            borderWidth: 'thin',
            borderStyle: 'solid',
            borderColor: 'border',
            borderRadius: 'medium',
          }}
        >
          <Card.Header css={{ padding: 'space.300', display: 'flex', justifyContent: 'space-between' }}>
            <H2 variant="title200">Financial Information</H2>
          </Card.Header>
          <Separator css={{ fullWidthWithInset: 'space.300' }} />
          <Card.Content
            className={vstack({
              alignItems: 'stretch',
              padding: 'space.300',
            })}
          >
            {shouldShowBalanceWarning && (
              <Box>
                <Banner variant="warning" css={{ mb: 'space.200' }}>
                  <Text>
                    It looks like you may not meet the $100,000 minimum balance required for the Betterment Advisor
                    Network.
                  </Text>
                </Banner>
              </Box>
            )}
            <Box
              css={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gridTemplateRows: 'auto auto auto',
                gap: 'space.200',
              }}
            >
              <VStack css={{ gap: 'space.100' }}>
                <Text variant="text50" color="text.weak">
                  Employment Status
                </Text>
                <Text>{employmentStatus}</Text>
              </VStack>
              <VStack css={{ gap: 'space.100' }}>
                <Text variant="text50" color="text.weak">
                  Occupation
                </Text>
                <Text>{occupation}</Text>
              </VStack>

              <VStack css={{ gap: 'space.100' }}>
                <Text variant="text50" color="text.weak">
                  Annual pretax income
                </Text>
                <Text>{annualPretaxIncome}</Text>
              </VStack>
              <VStack css={{ gap: 'space.100' }}>
                <Text variant="text50" color="text.weak">
                  Spouse annual pretax income
                </Text>
                <Text>{spouseAnnualPretaxIncome}</Text>
              </VStack>

              <VStack css={{ gap: 'space.100' }}>
                <Text variant="text50" color="text.weak">
                  Household investable assets
                </Text>
                <HStack css={{ gap: 'space.50' }}>
                  <Text>{householdInvestableAssets}</Text>

                  {shouldShowBalanceWarning && (
                    <Icon
                      name="WarningFilled"
                      size="inline"
                      color="icon.warning"
                      css={{ marginTop: 'space.50' }}
                      data-testid="warningIcon"
                    />
                  )}
                </HStack>
              </VStack>
              <VStack css={{ gap: 'space.100' }}>
                <Text variant="text50" color="text.weak">
                  Tax filing status
                </Text>
                <Text>{taxFilingStatus}</Text>
              </VStack>
            </Box>
          </Card.Content>
        </Card>
        <Card css={{ borderWidth: 'thin', borderStyle: 'solid', borderColor: 'border', borderRadius: 'medium' }}>
          <Card.Header css={{ padding: 'space.300', display: 'flex', justifyContent: 'space-between' }}>
            <Heading as="h2" variant="title200">
              Location
            </Heading>
          </Card.Header>
          <Separator css={{ fullWidthWithInset: 'space.300' }} />
          <Card.Content css={{ padding: 'space.300' }}>
            <VStack css={{ gap: 'space.100' }}>
              <Text variant="text50" color="text.weak">
                Address
              </Text>
              <VStack css={{ gap: 'space.0' }}>
                <Text>{residentialAddress?.line1}</Text>
                <Text>
                  {residentialAddress?.city}, {residentialAddress?.state.code} {residentialAddress?.postalCode}
                </Text>
              </VStack>
            </VStack>
          </Card.Content>
        </Card>
        <Button
          onClick={() => goToNextStep()}
          variant="solidPrimary"
          css={{
            maxWidth: '1/4',
            smDown: { width: '100%' },
            paddingY: 'space.200',
          }}
        >
          Continue
        </Button>
      </Box>
    </WorkflowLayout>
  );
};
